import React, { useState, useEffect } from "react";
import Header from "../../Components/Common/Header";
import Footer from "../../Components/Common/Footer";
import { Helmet } from "react-helmet-async";
import config from "../../config";
import { useNavigate } from "react-router-dom";

const Blog = () => {
  const [originalBlogs, setOriginalBlogs] = useState([]);
  const [allBlogs, setAllBlogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [category, setCategory] = useState([]);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const blogsPerPage = 6;

  useEffect(() => {
    const fetchAllBlog = async () => {
      try {
        const response = await fetch(`${config.baseUrl}/blogs`);
        if (!response.ok) {
          throw new Error("Failed to fetch blog data");
        }
        const result = await response.json();
        if (result.status === "success" && result.data.length > 0) {
          setOriginalBlogs(result.data);
          setAllBlogs(result.data);
        } else {
          setError("No data found");
        }
      } catch (err) {
        setError(err.message);
      }
    };

    const fetchCategory = async () => {
      try {
        const response = await fetch(`${config.baseUrl}/category`);
        if (!response.ok) {
          throw new Error("Failed to fetch blog data");
        }
        const result = await response.json();
        if (result.status === "success" && result.data.length > 0) {
          setCategory(result.data);
        } else {
          setError("No data found");
        }
      } catch (err) {
        setError(err.message);
      }
    };

    fetchAllBlog();
    fetchCategory();
  }, []);

  // Pagination Logic
  const indexOfLastBlog = currentPage * blogsPerPage;
  const indexOfFirstBlog = indexOfLastBlog - blogsPerPage;
  const currentBlogs = allBlogs.slice(indexOfFirstBlog, indexOfLastBlog);
  const totalPages = Math.ceil(allBlogs.length / blogsPerPage);

  const handleClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrev = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNext = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const navigate = useNavigate();

  const handleReadMore = (id, name) => {
    const slug = name.toLowerCase().replace(/\s+/g, "-");
    navigate(`/blogs/${slug}`, { state: { blogId: id } });
  };

  const handleSearch = () => {
    if (searchTerm.trim() === "") {
      setAllBlogs(originalBlogs);
      return;
    }

    const filteredBlogs = originalBlogs.filter((blog) =>
      blog.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setAllBlogs(filteredBlogs);
    setCurrentPage(1); // Reset to first page
  };

  const handleCancelSearch = () => {
    setSearchTerm("");
    setSelectedCategory(null);
    setAllBlogs(originalBlogs);
    setCurrentPage(1);
  };

  const handleCategorySelect = (categoryName) => {
    setSelectedCategory(categoryName);

    const filteredBlogs = originalBlogs.filter((blog) => {
      if (!blog.categories) return false;

      // Split blog categories by comma and trim spaces
      const blogCategories = blog.categories
        .split(",")
        .map((cat) => cat.trim().toLowerCase());
      return blogCategories.includes(categoryName.toLowerCase());
    });

    setAllBlogs(filteredBlogs);
    setCurrentPage(1); // Reset to first page
  };

  // Helper function to strip HTML and truncate text
const stripHtmlAndTruncate = (html, length) => {
  // Create a temporary div element
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  
  // Get text content and remove excessive whitespace
  const text = tmp.textContent || tmp.innerText || '';
  const cleanText = text.replace(/\s+/g, ' ').trim();
  
  // Truncate and add ellipsis if needed
  return cleanText.length > length 
    ? `${cleanText.substring(0, length)}...` 
    : cleanText;
};

  return (
    <div>
      <Helmet>
        <title>Blogs | Corpquick</title>
        <meta
          name="description"
          content="Explore our latest blogs and articles on various topics. Stay updated with the latest trends and insights."
        />
      </Helmet>
      <Header />
      <div className="common-banenr">
        <div className="common-content">
          <h1>Blogs</h1>
        </div>
      </div>
      {selectedCategory && (
        <h2 className="category-type">{`Category: ${selectedCategory}`}</h2>
      )}
      <div className="storylist">
        <div className="container">
          <div className="row">
            <div className="bloglisting col-md-8">
              <div className="row" id="blogs">
                {error && <p>{error}</p>}
                {currentBlogs.map((blog, index) => (
                  <div className="col-md-12" key={index}>
                    <div className="card">
                      <div className="card-body row">
                        <div className="col-md-6">
                          <div className="blogimage">
                            <img
                              src={`${config.imgURL}/data/uploads/${blog.image}`}
                              alt={blog.name}
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <h4 className="card-title">
                            <p className="text-dark">{blog.name}</p>
                          </h4>
                          <div className="card-text">
                           {blog.description && (
            <div className="blog-preview">
              {stripHtmlAndTruncate(blog.description, 200)}
            </div>
          )}
                          </div>
                          <button
                            className="viewallbtn"
                            onClick={() => handleReadMore(blog.id, blog.name)}
                          >
                            Read More
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Search and Category */}
            <div className="col-md-4">
              <div className="searchbar">
                <input
                  type="text"
                  id="site-search"
                  name="q"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button type="button" onClick={handleSearch}>
                  Search
                </button>
                {searchTerm && (
                  <button
                    type="button"
                    className="bg-danger"
                    onClick={handleCancelSearch}
                  >
                    Cancel
                  </button>
                )}
              </div>
              <div className="recentblogs">
                <h4>Category</h4>
                <ul>
                  {category &&
                    category.map((item, index) => (
                      <li key={index}>
                        <button
                          className={
                            selectedCategory === item.name
                              ? "active-category"
                              : ""
                          }
                          onClick={() => handleCategorySelect(item.name)}
                        >
                          {item.name}
                        </button>
                      </li>
                    ))}
                  <li>
                    <button
                      onClick={handleCancelSearch}
                      className="text-success"
                    >
                      Show All Blogs
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Pagination */}
          {allBlogs.length > blogsPerPage && (
            <div className="pagination">
              <button onClick={handlePrev} disabled={currentPage === 1}>
                &laquo;
              </button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => handleClick(i + 1)}
                  className={currentPage === i + 1 ? "active" : ""}
                >
                  {i + 1}
                </button>
              ))}
              <button
                onClick={handleNext}
                disabled={currentPage === totalPages}
              >
                &raquo;
              </button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Blog;
